import { bnPageHead } from "@/lib/bn-head";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { BN_POSTS } from "@/content/bn/posts";
import { SITE } from "@/content/site";
import { ArrowUpRight } from "lucide-react";

const URL = `${SITE.url}/bn`;
const TITLE = "ফ্রিল্যান্সার তামাল — বাংলা ডিজিটাল মার্কেটিং গাইড";
const DESC =
  "বাংলাদেশে ডিজিটাল মার্কেটিং, SEO, ফেসবুক অ্যাডস এবং ফ্রিল্যান্সিং শেখার সম্পূর্ণ বাংলা রিসোর্স — একজন প্র্যাকটিসিং কনসালট্যান্টের ব্লগ।";

export const Route = createFileRoute("/bn/")({
  head: () =>
    bnPageHead({
      path: "/bn",
      enPath: "/",
      title: TITLE,
      desc: DESC,
      keywords: ["seo bangla", "ডিজিটাল মার্কেটিং শেখা", "ফ্রিল্যান্সিং বাংলা"],
      suffix: false,
      image: { url: `${SITE.url}/og/bn.jpg`, alt: TITLE },
    }),
  component: BnHome,
});

function BnHome() {
  const latest = [...BN_POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${URL}/#website-bn`,
    name: "ফ্রিল্যান্সার তামাল",
    url: URL,
    description: DESC,
    inLanguage: "bn-BD",
  };

  return (
    <>
      <JsonLd data={websiteSchema} />
      <Section className="pt-8 pb-16">
        <div className="mb-8 text-xs uppercase tracking-wider text-muted-foreground">
          <Link to="/" className="hover:text-foreground">EN</Link>
          <span className="mx-2">·</span>
          <span className="text-foreground">বাংলা</span>
        </div>

        <header className="max-w-3xl">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            বাংলায় ডিজিটাল মার্কেটিং — <span className="text-gradient">শূন্য থেকে পেশাদার</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            SEO, ফেসবুক অ্যাডস, কন্টেন্ট মার্কেটিং, ফ্রিল্যান্সিং — বাংলাদেশের প্র্যাকটিসিং কনসালট্যান্টের বাংলা গাইড।
            থিওরি নয়, যা সত্যিই কাজ করে শুধু সেটাই।
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/bn/blog"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium"
            >
              সব আর্টিকেল পড়ুন
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/bn/contact"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium"
            >
              পরামর্শের জন্য যোগাযোগ
            </Link>
          </div>
        </header>
      </Section>

      <Section className="pb-24">
        <h2 className="font-display text-2xl mb-6">সাম্প্রতিক আর্টিকেল</h2>
        <div className="grid gap-6 sm:grid-cols-2">
          {latest.map((p) => (
            <Link
              key={p.slug}
              to="/bn/blog/$slug"
              params={{ slug: p.slug }}
              className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
            >
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                {p.category} · {p.readMin} মিনিট পড়া
              </div>
              <h3 className="mt-3 font-display text-xl leading-snug group-hover:text-primary transition">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary">
                পড়ুন <ArrowUpRight className="h-4 w-4" />
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </>
  );
}
